import React, { useEffect, useState } from "react";
import { WebMascot } from "../components/WebMascot";

// Bedtime audio-only player — ported from the Mapa design handoff
// (audio-player.jsx / audio-player.css), adapted to drive the REAL media
// element via props instead of the prototype's mock interval. Single-track:
// the multi-story carousel / up-next from the prototype collapse to one
// uploaded audio, so prev = restart and there's no next/up-next list. The
// sleep timer genuinely pauses playback.

const SLEEP_OPTIONS = [0, 5, 15, 30, 45]; // minutes; 0 = off

// Calm fixed palette (the prototype's "moonbeam" story): [bg, mid, glow].
const PAL = ["#3A386B", "#5C5A8C", "#9D9BC8"] as const;

const fmt = (s: number) =>
  `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}`;

type IconName =
  | "play"
  | "pause"
  | "prev"
  | "next"
  | "moon"
  | "timer"
  | "list"
  | "x";

function AudioIcon({
  name,
  size = 24,
  color = "#fff",
}: {
  name: IconName;
  size?: number;
  color?: string;
}) {
  const f = { fill: color };
  const map: Record<IconName, React.ReactNode> = {
    play: <path d="M8 5l12 7-12 7z" {...f} />,
    pause: (
      <g>
        <rect x="7" y="5" width="4" height="14" rx="1" {...f} />
        <rect x="13" y="5" width="4" height="14" rx="1" {...f} />
      </g>
    ),
    prev: (
      <g>
        <rect x="6" y="5" width="2.6" height="14" rx="1" {...f} />
        <path d="M20 5l-10 7 10 7z" {...f} />
      </g>
    ),
    next: (
      <g>
        <rect x="15.4" y="5" width="2.6" height="14" rx="1" {...f} />
        <path d="M4 5l10 7-10 7z" {...f} />
      </g>
    ),
    list: (
      <g>
        <path d="M4 7h11M4 12h11M4 17h7" stroke={color} strokeWidth="2" strokeLinecap="round" />
        <path d="M18 12l3 2-3 2z" {...f} />
      </g>
    ),
    moon: (
      <path
        d="M20 14.5A8 8 0 0 1 9.5 4 8 8 0 1 0 20 14.5z"
        stroke={color}
        strokeWidth="2"
        fill="none"
        strokeLinejoin="round"
      />
    ),
    timer: (
      <g>
        <circle cx="12" cy="13" r="8" stroke={color} strokeWidth="2" fill="none" />
        <path d="M12 9v4l2.5 2" stroke={color} strokeWidth="2" strokeLinecap="round" fill="none" />
        <path d="M9 2h6" stroke={color} strokeWidth="2" strokeLinecap="round" />
      </g>
    ),
    x: <path d="M6 6l12 12M18 6L6 18" stroke={color} strokeWidth="2.4" strokeLinecap="round" />,
  };
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" style={{ display: "block" }}>
      {map[name]}
    </svg>
  );
}

type Props = {
  title: string;
  blurb?: string;
  seconds: number;
  duration: number;
  playing: boolean;
  onTogglePlay: () => void;
  onSeek: (sec: number) => void;
  onPause: () => void;
  onClose?: () => void;
  // Up-next: other bedtime audios the parent can jump to. `onNext` plays the
  // first one (transport ▶▶); `onSelect` plays a specific id from the sheet.
  tracks?: { id: string; title: string }[];
  onNext?: () => void;
  onPrev?: () => void;
  onSelect?: (id: string) => void;
};

export function WebBedtimeAudio({
  title,
  blurb,
  seconds,
  duration,
  playing,
  onTogglePlay,
  onSeek,
  onPause,
  onClose,
  tracks = [],
  onNext,
  onPrev,
  onSelect,
}: Props) {
  const total = Math.max(1, duration);
  const t = Math.min(seconds, total);

  const [sleepMin, setSleepMin] = useState(0);
  const [sleepLeft, setSleepLeft] = useState(0);
  const [showTimer, setShowTimer] = useState(false);
  const [showList, setShowList] = useState(false);

  // Sleep-timer countdown — only while playing + armed. On reaching zero we
  // pause playback (the screen-rest gradient stays so the room stays dim).
  useEffect(() => {
    if (!playing || sleepMin === 0) return;
    const id = setInterval(() => {
      setSleepLeft((s) => {
        if (s <= 1) {
          onPause();
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(id);
  }, [playing, sleepMin, onPause]);

  const setTimer = (m: number) => {
    setSleepMin(m);
    setSleepLeft(m * 60);
    setShowTimer(false);
  };

  const [bg, mid, glow] = PAL;
  const artSize = 240;

  return (
    <div
      className="ka-player ka-web"
      style={{
        background: `radial-gradient(120% 90% at 50% 18%, ${mid} 0%, ${bg} 55%, #0c0b16 100%)`,
      }}
    >
      <style>{KA_CSS}</style>

      {/* drifting stars */}
      <div className="ka-stars">
        {Array.from({ length: 26 }).map((_, i) => (
          <span
            key={i}
            style={{
              left: `${(i * 53) % 100}%`,
              top: `${(i * 37) % 70}%`,
              width: (i % 3) + 2,
              height: (i % 3) + 2,
              opacity: 0.3 + (i % 4) * 0.16,
              animationDelay: `${(i % 6) * 0.5}s`,
            }}
          />
        ))}
      </div>

      {/* header */}
      <div className="ka-head">
        <div className="ka-eyebrow">
          <AudioIcon name="moon" size={16} color="rgba(255,255,255,0.7)" />
          Bedtime story
        </div>
        {onClose && (
          <button className="ka-x" onClick={onClose} aria-label="Close">
            <AudioIcon name="x" size={20} color="rgba(255,255,255,0.8)" />
          </button>
        )}
      </div>

      {/* art */}
      <div className="ka-art-wrap">
        <div
          className={"ka-art " + (playing ? "breathe" : "")}
          style={{
            width: artSize,
            height: artSize,
            boxShadow: `0 0 80px ${glow}66, inset 0 0 60px rgba(0,0,0,0.25)`,
          }}
        >
          <span className="ka-ring" style={{ borderColor: `${glow}40` }} />
          <span className="ka-ring r2" style={{ borderColor: `${glow}26` }} />
          <div
            className="ka-art-inner"
            style={{ background: `linear-gradient(160deg, ${glow}, ${bg})` }}
          >
            <div className={playing ? "bob" : ""}>
              <WebMascot mood="sleep" color="#fff" size={artSize * 0.5} />
            </div>
          </div>
        </div>
      </div>

      {/* title */}
      <div className="ka-meta">
        <div className="ka-title">{title}</div>
        <div className="ka-narr">
          Audio only{blurb ? " · " + blurb : ""}
        </div>
      </div>

      {/* scrubber */}
      <div className="ka-scrub">
        <span className="ka-time">{fmt(t)}</span>
        <div
          className="ka-track"
          onClick={(e) => {
            const r = e.currentTarget.getBoundingClientRect();
            onSeek(
              Math.max(0, Math.min(total, ((e.clientX - r.left) / r.width) * total)),
            );
          }}
        >
          <div className="ka-fill" style={{ width: `${(t / total) * 100}%` }}>
            <span className="ka-knob" />
          </div>
        </div>
        <span className="ka-time">-{fmt(total - t)}</span>
      </div>

      {/* transport — single track: prev restarts, no next */}
      <div className="ka-transport">
        <button
          className="ka-sec"
          onClick={() => {
            // Standard transport behaviour: restart if we're more than a few
            // seconds in, otherwise jump to the previous story.
            if (t > 3 || !onPrev) onSeek(0);
            else onPrev();
          }}
          aria-label="Previous"
        >
          <AudioIcon name="prev" size={26} />
        </button>
        <button
          className="ka-play"
          onClick={onTogglePlay}
          aria-label={playing ? "Pause" : "Play"}
        >
          <AudioIcon name={playing ? "pause" : "play"} size={34} color="#2A2950" />
        </button>
        {onNext ? (
          <button className="ka-sec" onClick={onNext} aria-label="Next story">
            <AudioIcon name="next" size={26} />
          </button>
        ) : (
          <span className="ka-sec" style={{ visibility: "hidden" }}>
            <AudioIcon name="prev" size={26} />
          </span>
        )}
      </div>

      {/* bottom utility bar */}
      <div className="ka-utils">
        <button
          className={"ka-util " + (sleepMin ? "active" : "")}
          onClick={() => setShowTimer(true)}
        >
          <AudioIcon
            name="timer"
            size={18}
            color={sleepMin ? "#FFD86B" : "rgba(255,255,255,0.8)"}
          />
          <span style={{ color: sleepMin ? "#FFD86B" : undefined }}>
            {sleepMin ? `Sleep in ${fmt(sleepLeft)}` : "Sleep timer"}
          </span>
        </button>
        {tracks.length > 0 && (
          <button className="ka-util" onClick={() => setShowList(true)}>
            <AudioIcon name="list" size={18} />
            <span>Up next</span>
          </button>
        )}
        <div className="ka-util">
          <AudioIcon name="moon" size={18} color="rgba(255,255,255,0.7)" />
          <span>Screen rests while playing</span>
        </div>
      </div>

      {/* sleep timer sheet */}
      {showTimer && (
        <div className="ka-overlay" onClick={() => setShowTimer(false)}>
          <div className="ka-sheet" onClick={(e) => e.stopPropagation()}>
            <div className="ka-sheet-h">
              <AudioIcon name="timer" size={20} color="#FFD86B" /> Sleep timer
            </div>
            <div className="ka-sheet-sub">
              Audio stops automatically when the timer ends.
            </div>
            <div className="ka-timer-opts">
              {SLEEP_OPTIONS.map((m) => (
                <button
                  key={m}
                  className={"ka-topt " + (sleepMin === m ? "on" : "")}
                  onClick={() => setTimer(m)}
                >
                  {m === 0 ? "Off" : `${m}m`}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* up next sheet */}
      {showList && (
        <div className="ka-overlay" onClick={() => setShowList(false)}>
          <div className="ka-sheet" onClick={(e) => e.stopPropagation()}>
            <div className="ka-sheet-h">
              <AudioIcon name="list" size={20} /> Up next
            </div>
            <div className="ka-list">
              {tracks.map((tr) => (
                <button
                  key={tr.id}
                  className="ka-lrow"
                  onClick={() => {
                    onSelect?.(tr.id);
                    setShowList(false);
                  }}
                >
                  <span className="ka-lart">
                    <AudioIcon name="moon" size={20} />
                  </span>
                  <span className="ka-ltx">
                    <span className="ka-llab">{tr.title}</span>
                    <span className="ka-lsub">Bedtime audio</span>
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default WebBedtimeAudio;

// Ported from audio-player.css (web surface), self-contained so the player
// drops into any screen without a global stylesheet dependency. var(--font)/
// var(--display) collapse to `inherit` (kidly's Nunito is already the body
// font).
const KA_CSS = `
.ka-player{ position:absolute; inset:0; overflow:hidden; color:#fff; font-family:inherit; display:flex; flex-direction:column; align-items:center; }
.ka-web{ padding:28px 64px 32px; }
.ka-stars{ position:absolute; inset:0; pointer-events:none; }
.ka-stars span{ position:absolute; border-radius:50%; background:#fff; animation:kaTwinkle 4s ease-in-out infinite; }
@keyframes kaTwinkle{ 0%,100%{ opacity:0.2; } 50%{ opacity:0.9; } }
.ka-head{ width:100%; flex-shrink:0; display:flex; align-items:center; justify-content:space-between; z-index:2; }
.ka-eyebrow{ display:flex; align-items:center; gap:8px; font-weight:800; letter-spacing:0.06em; text-transform:uppercase; color:rgba(255,255,255,0.7); font-size:13px; }
.ka-x{ width:40px; height:40px; border:0; background:rgba(255,255,255,0.12); cursor:pointer; border-radius:50%; display:grid; place-items:center; }
.ka-art-wrap{ flex:1; display:grid; place-items:center; width:100%; z-index:1; }
.ka-art{ position:relative; border-radius:50%; display:grid; place-items:center; }
.ka-art.breathe{ animation:kaBreathe 6s ease-in-out infinite; }
@keyframes kaBreathe{ 0%,100%{ transform:scale(1); } 50%{ transform:scale(1.035); } }
.ka-ring{ position:absolute; inset:-18px; border-radius:50%; border:2px solid; animation:kaPulse 5s ease-out infinite; }
.ka-ring.r2{ inset:-40px; animation-delay:1.2s; }
@keyframes kaPulse{ 0%{ transform:scale(0.96); opacity:0.8; } 100%{ transform:scale(1.12); opacity:0; } }
.ka-art-inner{ width:78%; height:78%; border-radius:50%; display:grid; place-items:center; box-shadow:inset 0 -10px 30px rgba(0,0,0,0.25); }
.ka-meta{ text-align:center; z-index:2; flex-shrink:0; margin:8px 0 20px; }
.ka-title{ font-weight:900; letter-spacing:-0.02em; font-size:30px; }
.ka-narr{ color:rgba(255,255,255,0.6); font-weight:600; font-size:14px; margin-top:6px; }
.ka-scrub{ display:flex; align-items:center; gap:12px; width:100%; max-width:560px; z-index:2; flex-shrink:0; }
.ka-time{ font-size:12px; font-weight:700; color:rgba(255,255,255,0.6); font-variant-numeric:tabular-nums; min-width:38px; text-align:center; }
.ka-track{ flex:1; height:6px; border-radius:6px; background:rgba(255,255,255,0.18); cursor:pointer; }
.ka-fill{ position:relative; height:100%; border-radius:6px; background:#FFD86B; }
.ka-knob{ position:absolute; right:-7px; top:50%; transform:translateY(-50%); width:14px; height:14px; border-radius:50%; background:#fff; box-shadow:0 2px 6px rgba(0,0,0,0.3); }
.ka-transport{ display:flex; align-items:center; justify-content:center; gap:40px; margin:24px 0; z-index:2; flex-shrink:0; }
.ka-sec{ border:0; background:transparent; cursor:pointer; display:grid; place-items:center; opacity:0.9; }
.ka-sec:active{ transform:scale(0.92); }
.ka-play{ width:76px; height:76px; border:0; cursor:pointer; border-radius:50%; background:#fff; display:grid; place-items:center; box-shadow:0 10px 30px rgba(0,0,0,0.35); transition:transform 140ms; }
.ka-play:active{ transform:scale(0.94); }
.ka-utils{ display:flex; align-items:center; justify-content:center; width:100%; gap:36px; font-size:13px; z-index:2; flex-shrink:0; flex-wrap:wrap; }
.ka-util{ display:inline-flex; align-items:center; gap:7px; border:0; background:transparent; cursor:pointer; color:rgba(255,255,255,0.8); font:inherit; font-weight:700; }
.ka-util.active span{ font-weight:800; }
.ka-overlay{ position:absolute; inset:0; z-index:10; background:rgba(8,6,16,0.5); backdrop-filter:blur(3px); display:flex; align-items:flex-end; justify-content:center; animation:kaFade 200ms ease; }
@keyframes kaFade{ from{ opacity:0; } to{ opacity:1; } }
.ka-sheet{ width:100%; background:#1b1830; border-radius:26px 26px 0 0; padding:22px 22px 26px; animation:kaUp 320ms cubic-bezier(.16,1,.3,1); box-shadow:0 -10px 40px rgba(0,0,0,0.4); }
@keyframes kaUp{ from{ transform:translateY(100%); } to{ transform:translateY(0); } }
.ka-sheet-h{ display:flex; align-items:center; gap:10px; font-weight:900; font-size:19px; }
.ka-sheet-sub{ color:rgba(255,255,255,0.55); font-size:13px; font-weight:600; margin:4px 0 16px; }
.ka-timer-opts{ display:flex; gap:8px; }
.ka-topt{ flex:1; border:0; cursor:pointer; background:rgba(255,255,255,0.1); color:#fff; font:inherit; font-weight:900; font-size:15px; padding:14px 0; border-radius:14px; }
.ka-topt.on{ background:#FFD86B; color:#2A2950; }
.ka-list{ display:flex; flex-direction:column; gap:6px; max-height:50vh; overflow-y:auto; }
.ka-lrow{ display:flex; align-items:center; gap:12px; border:0; background:transparent; cursor:pointer; text-align:left; padding:8px; border-radius:14px; font:inherit; }
.ka-lrow:hover{ background:rgba(255,255,255,0.08); }
.ka-lart{ width:44px; height:44px; border-radius:12px; display:grid; place-items:center; flex:0 0 auto; background:linear-gradient(160deg,#9D9BC8,#3A386B); }
.ka-ltx{ flex:1; display:flex; flex-direction:column; min-width:0; }
.ka-llab{ font-weight:800; font-size:15px; color:#fff; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
.ka-lsub{ font-size:12px; font-weight:600; color:rgba(255,255,255,0.55); }
`;
